import React, { useRef } from "react";
import { Dropdown, FormField, FormGroup, Input, Radio, SemanticWIDTHS } from "semantic-ui-react";
import { OfficialAccountAddressDataType } from "./OfficialAccountAddressForm";

export const BUSINESS_MODEL_OPTIONS = [
  { key: "INDIVIDUAL", value: "INDIVIDUAL", text: "บุคคลธรรมดา" },
  { key: "LEGAL_ENTITY", value: "LEGAL_ENTITY", text: "นิติบุคคล" },
];

type BusinessInformationFormProps = {
  detail: OfficialAccountAddressDataType;
  readOnly?: boolean;
  error?: Record<string, any>;
  businessTypeWidth?: SemanticWIDTHS;
  width?: SemanticWIDTHS;
  requiredTextError?: (isRequired: boolean) => React.ReactNode;
  handleChangeValue?: (e?: any, data?: any) => void;
  languageUX?: "en" | "th";
};

const BusinessInformationForm = (props: BusinessInformationFormProps) => {
  const business = props.detail.business;
  const isIndividual = business?.business_type === "INDIVIDUAL";
  const isLegal = business?.business_type === "LEGAL_ENTITY";
  const isBranch = business?.branch_type === "BRANCH";

  const branchOptions = [
    { label: "สำนักงานใหญ่", value: "HEADQUARTER" },
    { label: "สาขา", value: "BRANCH" },
  ];

  return (
    <>
      <FormGroup inline>
        {!!props.width && (
          <FormField width={props.width} required>
            <label>รูปแบบธุรกิจ</label>
          </FormField>
        )}

        <FormField width={props.businessTypeWidth} required>
          {!props.width && <label>รูปแบบธุรกิจ</label>}

          <div style={{ display: "grid" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              <Dropdown
                disabled={props.readOnly}
                name="business.business_type"
                placeholder="-- กรุณาเลือกรูปแบบธุรกิจ --"
                value={business?.business_type}
                selection
                options={BUSINESS_MODEL_OPTIONS}
                onChange={props.handleChangeValue}
                style={{ minWidth: 250 }}
                error={props.error?.business_type}
              />

              {isLegal && (
                <FormField
                  style={{ display: "flex", alignItems: "center" }}
                  error={props.error?.branch_type}
                >
                  {branchOptions.map((opt) => (
                    <Radio
                      key={opt.value || "NONE"}
                      label={opt.label}
                      name="business.branch_type"
                      value={opt.value}
                      checked={
                        opt.custom
                          ? business?.branch_type === "" || business?.branch_type === null
                          : business?.branch_type === opt.value
                      }
                      onChange={
                        opt.custom
                          ? () =>
                              props.handleChangeValue?.(null, {
                                name: "business.branch_type",
                                value: "",
                              })
                          : props.handleChangeValue
                      }
                      style={{ minWidth: "max-content", marginRight: 10 }}
                    />
                  ))}
                </FormField>
              )}

              {isBranch && (
                <FormField error={!!props.error?.branch_name}>
                  <Input
                    name="business.branch_name"
                    placeholder="ระบุรหัสสาขา หรือ ชื่อสาขา"
                    value={business?.branch_name}
                    onChange={props.handleChangeValue}
                    disabled={props.readOnly}
                    style={{ minWidth: 200 }}
                  />
                </FormField>
              )}
            </div>

            {props.requiredTextError?.(
              props.error?.business_type || props.error?.branch_type || props.error?.branch_name
            )}
          </div>
        </FormField>
      </FormGroup>

      {(props.detail.business?.business_type === "INDIVIDUAL" ||
        props.detail.business?.business_type === "LEGAL_ENTITY") && (
        <FormGroup inline>
          {!!props.width && (
            <FormField width={props.width} required>
              <label>
                {props.detail.business?.business_type === "INDIVIDUAL"
                  ? "ชื่อ-นามสกุล"
                  : "ชื่อธุรกิจ"}
              </label>
            </FormField>
          )}

          <FormField
            width={props.width ? 8 : 11}
            inline
            required
            style={{ display: "flex", alignItems: "center" }}
          >
            {!props.width && <label>{isIndividual ? "ชื่อ-นามสกุล" : "ชื่อธุรกิจ"}</label>}

            <div style={{ display: "grid", width: "100%" }}>
              {isIndividual ? (
                <>
                  <div style={{ display: "flex" }}>
                    <FormField width={16}>
                      <Input
                        name="business.first_name"
                        placeholder="กรุณาระบุชื่อ"
                        value={business?.first_name}
                        onChange={props.handleChangeValue}
                        disabled={props.readOnly}
                        fluid
                        style={{ marginRight: 20 }}
                        error={props.error?.first_name}
                      />
                    </FormField>

                    <FormField width={16}>
                      <Input
                        name="business.last_name"
                        placeholder="กรุณาระบุนามสกุล"
                        value={business?.last_name}
                        onChange={props.handleChangeValue}
                        disabled={props.readOnly}
                        fluid
                        error={props.error?.last_name}
                      />
                    </FormField>
                  </div>

                  <div style={{ display: "flex" }}>
                    <FormField width={16}>
                      {props.requiredTextError?.(props.error?.first_name)}
                    </FormField>
                    <FormField width={16}>
                      {props.requiredTextError?.(props.error?.last_name)}
                    </FormField>
                  </div>
                </>
              ) : (
                <>
                  <FormField width={16}>
                    <Input
                      name="business.business_name"
                      placeholder="ชื่อธุรกิจ"
                      value={business?.business_name}
                      onChange={props.handleChangeValue}
                      disabled={props.readOnly}
                      error={props.error?.business_name}
                    />
                  </FormField>

                  <FormField width={16}>
                    {props.requiredTextError?.(props.error?.business_name)}
                  </FormField>
                </>
              )}
            </div>
          </FormField>
        </FormGroup>
      )}

      <FormGroup inline>
        {!!props.width && (
          <FormField width={props.width} required>
            <label>เลขประจำตัวผู้เสียภาษี</label>
          </FormField>
        )}
        <FormField width={11} inline required error={props.error?.tax_payer_id ?? false}>
          {!props.width && <label>เลขประจำตัวผู้เสียภาษี</label>}
          <div style={{ display: "grid" }}>
            <div style={{ display: "flex" }}>
              {Array.from({ length: 13 }).map((_, index) => {
                const extraMargin = [0, 4, 9].includes(index) ? 15 : 0;
                const rawTax = props.detail.business?.tax_payer_id || "";
                const taxIdArray = rawTax.padEnd(13, " ").split("");
                return (
                  <Input
                    key={index}
                    name="business.tax_payer_id"
                    id={`Business-TaxID-${index}`}
                    value={taxIdArray[index] === " " ? "" : taxIdArray[index]}
                    disabled={props.readOnly}
                    style={{
                      width: "40px",
                      textAlign: "center",
                      marginRight: extraMargin,
                    }}
                    input={{ type: "text", maxLength: 1 }}
                    onChange={(e) => {
                      const digit = e.target.value.replace(/\D/g, "").slice(0, 1);
                      let arr = (props.detail.business?.tax_payer_id || "")
                        .padEnd(13, " ")
                        .split("");
                      arr[index] = digit || " ";

                      props.handleChangeValue?.(e, {
                        name: "business.tax_payer_id",
                        value: arr.join(""),
                      });

                      if (digit && index < 12) {
                        document.getElementById(`Business-TaxID-${index + 1}`)?.focus();
                      }
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Backspace") {
                        const arr = (props.detail.business?.tax_payer_id || "")
                          .padEnd(13, " ")
                          .split("");
                        const current = arr[index];

                        if ((current === " " || current === "") && index > 0) {
                          const prevInput = document.getElementById(
                            `Business-TaxID-${index - 1}`
                          ) as HTMLInputElement | null;
                          prevInput?.focus();
                          e.preventDefault();
                        }
                      }
                    }}
                  />
                );
              })}
            </div>
            <div>{props.requiredTextError?.(props.error?.tax_payer_id)}</div>
          </div>
        </FormField>
      </FormGroup>
    </>
  );
};

export default BusinessInformationForm;
